"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useRef, useState, useCallback, useSyncExternalStore, ReactNode } from "react";
import { ConsentState } from "./ConsentTypes";
import { reportConsentChoice, reportGpcDetected } from "./consentOutcome";
import { buildSalesforceConsents, STORAGE_KEY } from "./ConsentProvider_helpers";
import { readGlobalPrivacyControl, runPendingTrackingCookieCleanup } from "./privacyChoices";
type ConsentCommand = "default" | "update";
function emitConsentSignal(command: ConsentCommand, payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag("consent", command, payload);
    return;
  }
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  window.dataLayer.push(["consent", command, payload]);
}
function storagePayload(marketing: boolean, analytics: boolean): Record<string, string> {
  return {
    ad_storage: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied"
  };
}
interface ConsentContextType {
  /** Effective advertising consent: a GPC signal forces it to "denied". */
  marketingConsent: ConsentState;
  analyticsConsent: ConsentState;
  /** The browser sent a Global Privacy Control opt-out signal. */
  gpcDetected: boolean;
  setConsent: (marketing: boolean, analytics: boolean) => void;
  isLoading: boolean;
}
const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

// Stable no-op subscribe for `useSyncExternalStore` — the snapshot never
// changes after the initial client render, so we don't need to register
// any listeners.
const emptySubscribe = (): (() => void) => () => {};
export function ConsentProvider(t0) {
  const $ = _c(22);
  const {
    children
  } = t0;
  const [consent, setConsentState] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  const isHydrated = useSyncExternalStore(emptySubscribe, _temp3, _temp4);
  const isLoading = !isHydrated;
  const gpcDetected = useSyncExternalStore(emptySubscribe, readGlobalPrivacyControl, _temp5);
  const marketingConsent = gpcDetected && consent.marketing === "granted" ? "denied" : consent.marketing;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp6, t2);
  const defaultEmittedRef = useRef(false);
  let t3;
  let t4;
  if ($[2] !== consent.analytics || $[3] !== consent.marketing || $[4] !== gpcDetected) {
    t3 = () => {
      if (defaultEmittedRef.current) {
        return;
      }
      const pending = consent.marketing === "pending" && consent.analytics === "pending";
      if (pending) {
        emitConsentSignal("default", {
          ...storagePayload(false, false),
          wait_for_update: 500
        });
        defaultEmittedRef.current = true;
      } else {
        if (gpcDetected) {
          emitConsentSignal("default", storagePayload(false, consent.analytics === "granted"));
          defaultEmittedRef.current = true;
        }
      }
    };
    t4 = [consent.marketing, consent.analytics, gpcDetected];
    $[2] = consent.analytics;
    $[3] = consent.marketing;
    $[4] = gpcDetected;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const resolveSalesforce = _temp7;
  let t5;
  let t6;
  if ($[7] !== consent.analytics || $[8] !== marketingConsent) {
    t5 = () => {
      if (marketingConsent !== "pending" && consent.analytics !== "pending") {
        resolveSalesforce(marketingConsent === "granted", consent.analytics === "granted");
      }
    };
    t6 = [marketingConsent, consent.analytics, resolveSalesforce];
    $[7] = consent.analytics;
    $[8] = marketingConsent;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== gpcDetected) {
    t7 = (marketing_0, analytics_0) => {
      const mState = marketing_0 ? "granted" : "denied";
      const aState = analytics_0 ? "granted" : "denied";
      const newState = {
        marketing: mState,
        analytics: aState
      };
      setConsentState(newState);
      reportConsentChoice(marketing_0, analytics_0);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      emitConsentSignal("update", storagePayload(marketing_0 && !gpcDetected, analytics_0));
    };
    $[11] = gpcDetected;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const setConsent = t7;
  let t8;
  if ($[13] !== consent.analytics || $[14] !== gpcDetected || $[15] !== isLoading || $[16] !== marketingConsent || $[17] !== setConsent) {
    t8 = {
      marketingConsent,
      analyticsConsent: consent.analytics,
      gpcDetected,
      setConsent,
      isLoading
    };
    $[13] = consent.analytics;
    $[14] = gpcDetected;
    $[15] = isLoading;
    $[16] = marketingConsent;
    $[17] = setConsent;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== children || $[20] !== t8) {
    t9 = <ConsentContext.Provider value={t8}>{children}</ConsentContext.Provider>;
    $[19] = children;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp7(marketing, analytics) {
  window.SalesforceInteractions?.updateConsents(buildSalesforceConsents(marketing, analytics));
}
function _temp6() {
  runPendingTrackingCookieCleanup();
}
function _temp5() {
  return false;
}
function _temp4() {
  return false;
}
function _temp3() {
  return true;
}
function _temp2() {
  reportGpcDetected();
}
function _temp() {
  if (typeof window === "undefined") {
    return {
      marketing: "pending",
      analytics: "pending"
    };
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return {
    marketing: "pending",
    analytics: "pending"
  };
}
export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}